/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Button, Grid, IconButton, Tooltip } from '@mui/material';

import { CopyToClipboard } from '../../../../components/CopyToClipboard/component';
import { HUSpin } from '../../../../components/HUSpin/HUSpin';
import { SimpleConfirmModal } from '../../../../components/SimpleConfirmModal/SimpleConfirmModal';
import { Namespaces } from '../../../../i18n';
import { TextPlaceholder } from '../TextPlaceholder/TextPlaceholder';
import { createHiddenPasswordStyle } from './HiddenPassword.styles';

interface IProps {
  password: string;
  defaultShow?: boolean;
  onGeneratePassword: () => Promise<any>;
  showGenerateButton: boolean;
  disabled: boolean;
}

export const HiddenPassword = memo<IProps>(props => {
  const { password, defaultShow = false, onGeneratePassword, disabled } = props;
  const { t } = useTranslation();
  const classes = createHiddenPasswordStyle();

  const [hidden, setHidden] = useState<boolean>(!defaultShow);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);

  useEffect(() => {
    setIsLoading(false);
  }, [password]);

  const generatePassword = async () => {
    setIsOpenModal(false);
    setIsLoading(true);
    await onGeneratePassword();
    setIsLoading(false);
  };

  const toggleHidden = () => setHidden(prevValue => !prevValue);

  const toggleModal = () => setIsOpenModal(prevValue => !prevValue);

  return (
    <HUSpin className={classes.spinner} spinning={isLoading} delay={50}>
      <Grid container classes={{ root: classes.container }}>
        {hidden ? <TextPlaceholder length={15} icon={<FiberManualRecordIcon />} /> : <span>{password}</span>}
        <CopyToClipboard text={password}>
          <IconButton size="small" classes={{ root: classes.copyIcon }}>
            <FileCopyIcon />
          </IconButton>
        </CopyToClipboard>
        {hidden ? (
          <Tooltip title={t(`${Namespaces.Cluster}:details.summary.show`)!}>
            <IconButton onClick={toggleHidden} size="small" classes={{ root: classes.visibilityIcon }}>
              <VisibilityOffIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title={t(`${Namespaces.Cluster}:details.summary.hide`)!}>
            <IconButton onClick={toggleHidden} size="small" classes={{ root: classes.visibilityIcon }}>
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        )}
        {props.showGenerateButton ? (
          <Button
            classes={{ root: classes.link }}
            disabled={disabled}
            onClick={toggleModal}
            data-cy="cluster-page-generate-password-button"
          >
            {t(`${Namespaces.Cluster}:generate_new`)}
          </Button>
        ) : null}
      </Grid>
      <SimpleConfirmModal
        open={isOpenModal}
        message={t(`${Namespaces.Common}:confirmation.generate_new_password`)}
        onCancel={toggleModal}
        onConfirm={generatePassword}
      />
    </HUSpin>
  );
});
